import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { generatePatientDisplayName } from "../TaskCard/TaskCard";
import { closeDialog } from "../../state/ducks/dialogs";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { enqueueSnackbar } from "../../state/ducks/notifiers";
import { toggleSentReferralFlagSuccess } from "../../state/ducks/referralsSent";
import { toggleReceivedReferralFlagSuccess } from "../../state/ducks/referralsReceived";
import useUpdateReferral from "../Form/hooks/useUpdateReferral";
import useSendReferralEventMessage from "../Form/hooks/useSendReferralEventMessage";
import { prepReferralForSubmission } from "../../utils";
import { REFERRAL_EVENT_MESSAGE_NAME } from "../../constants/referrals";

const flagReasons = [
  "Documentation",
  "Patient Information",
  "Referral Status",
  "Scheduling Information",
  "Other",
];

const unflagReasons = ["Resolved", "Unresolved"];

export default function FlagDialog() {
  const dispatch = useDispatch();
  const { updateReferral } = useUpdateReferral();
  const { sendReferralEventMessage } = useSendReferralEventMessage();

  const flagDialogIsOpen = useSelector((state) => state.dialogs.flag?.open);
  const referral = useSelector((state) => state.dialogs.flag?.referral);

  const isFlagged = referral?.flagged;
  const { handleSubmit, setValue, register, unregister } = useForm({
    defaultValues: {
      flagReason: null,
      flagComment: "",
    },
  });
  const [flagReason, setFlagReason] = useState(null);
  const [flagComment, setFlagComment] = useState("");

  useEffect(() => {
    register("flagReason", { required: true });
    register("flagComment", {
      validate: (value) => value === "" || (value && value.trim().length >= 1),
    });

    return () => {
      if (unregister) {
        unregister("flagReason");
        unregister("flagComment");
      }
      setFlagReason(null);
      setFlagComment("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register]);

  const generateFlagComment = ({ flagReason, flagComment }) => {
    let comment = flagReason;
    if (flagComment) {
      return comment.concat(" - ", flagComment);
    }
    return comment;
  };

  const onSubmit = async (formValues) => {
    if (!referral) {
      return;
    }
    const comment = generateFlagComment(formValues);
    const { id, clientId, type, flagged } = referral;

    await updateReferral({
      variables: {
        clientId,
        processInstanceId: id,
        referral: prepReferralForSubmission({ flagged }, type),
      },
    });
    if (type === "SENT") {
      dispatch(
        toggleSentReferralFlagSuccess(id, flagged, referral?.fullScreen)
      );
    } else {
      dispatch(
        toggleReceivedReferralFlagSuccess(id, flagged, referral?.fullScreen)
      );
    }
    await sendReferralEventMessage({
      variables: {
        clientId,
        processInstanceId: id,
        message: {
          messageName: REFERRAL_EVENT_MESSAGE_NAME.FLAG_REFERRAL,
          additionalInfo: {
            commentDescription: comment,
          },
        },
      },
    });
    closeFlagDialogAndReinit();
  };

  const onError = (error) => {
    dispatch(
      enqueueSnackbar({
        message: `Please complete all required form fields`,
        options: {
          variant: "error",
        },
      })
    );
  };

  const setFlagReasonSelect = (data) => {
    setValue("flagReason", data);
    setFlagReason(data);
  };
  const setFlagCommentForm = (data) => {
    setValue("flagComment", data);
    setFlagComment(data);
  };

  const closeFlagDialogAndReinit = () => {
    dispatch(closeDialog("flag"));
    setValue("flagReason", null);
    setValue("flagComment", "");
    setFlagReason(null);
    setFlagComment("");
  };

  return flagDialogIsOpen ? (
    <Dialog
      data-testid="flag-dialog"
      key={`${referral.id}_${isFlagged ? "unflag" : "flag"}_dialog`}
      open={true}
      onClose={closeFlagDialogAndReinit}
      aria-labelledby="form-dialog-title"
      scroll="body"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="form-dialog-title" disableTypography>
        <Typography component="h2" variant="h6" data-testid="flag-dialog-title">
          {isFlagged ? "Unflag" : "Flag"}
        </Typography>
        <Typography
          variant="subtitle1"
          data-testid="flag-dialog-subtitle"
        >{`${generatePatientDisplayName(
          referral.patient || referral.patientSnapshot
        )} (REF-${referral.displayableId})`}</Typography>
      </DialogTitle>
      <DialogContent>
        <form
          id="flag-action-dialog"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          <Autocomplete
            autoHighlight
            id="flagReason"
            options={isFlagged ? unflagReasons : flagReasons}
            name={"flagReason"}
            style={{ width: "100%" }}
            value={flagReason}
            onChange={(e, val) => setFlagReasonSelect(val)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${isFlagged ? "Unf" : "F"}lag Reason *`}
                variant="standard"
              />
            )}
          />
          <TextField
            id="flagComment"
            name={flagComment}
            key="flagComment"
            value={flagComment || ""}
            onChange={(e) => setFlagCommentForm(e?.target?.value)}
            required={flagReason === "Other" || flagReason === "Unresolved"}
            label={"Description"}
            fullWidth
            multiline={true}
            rows={2}
            variant="standard"
            margin="normal"
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="flag-dialog-cancel-button"
          id="cancel"
          key={`cancel_${isFlagged ? "unflag" : "flag"}`}
          type="cancel"
          color="primary"
          onClick={closeFlagDialogAndReinit}
        >
          Cancel
        </Button>
        <Button
          data-testid={`${isFlagged ? "unflag" : "flag"}-dialog-submit-button`}
          id="success"
          key={`button_${isFlagged ? "unflag" : "flag"}`}
          type="submit"
          form="flag-action-dialog"
          color="primary"
        >
          {isFlagged ? "Unflag" : "Flag"}
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
